import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useSearchParams } from "react-router-dom";
import { AnimatePresence, LayoutGroup, motion, useReducedMotion } from "motion/react";
import { useLenis } from "lenis/react";
import { ArrowDown, ArrowLeft, ArrowRight, ArrowUpRight, Play, X } from "@phosphor-icons/react";
import { EASE_OUT, MaskWords, PageFrame, Photo, Seo } from "../../kit.jsx";
import { MEDIA_LIBRARY, ORGANIZATION_LOGO, SITE_URL } from "../../data/seo.js";
import { AAROH_ALBUM_URL, AAROH_SUBFILTERS, FILTER_NOTES, MEDIA, PHOTO_FILTERS } from "./data.js";
import { RollLabel, formatDuration, pauseAllInline, pauseInline, playInline } from "./shared.jsx";
import "./stories.css";

const FOCUSABLE = 'a[href], button:not([disabled]), video[controls], [tabindex]:not([tabindex="-1"])';
const CATEGORY_LABEL = Object.fromEntries(AAROH_SUBFILTERS);

function itemLabel(item) {
  const parts = [item.programme];
  if (item.kind === "video") parts.push("Clip");
  else if (item.category) parts.push(CATEGORY_LABEL[item.category]);
  if (item.artwork) parts.push("Artwork");
  return parts.join(" · ");
}

function Lightbox({ items, index, openedId, onClose, onStep }) {
  const reduce = useReducedMotion();
  const lenis = useLenis();
  const dialogRef = useRef(null);
  const closeRef = useRef(null);
  const item = items[index];
  const shared = !reduce && item.id === openedId;

  useEffect(() => {
    pauseAllInline();
    closeRef.current?.focus({ preventScroll: true });
    lenis?.stop();
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      lenis?.start();
      document.body.style.overflow = previous;
    };
  }, [lenis]);

  useEffect(() => {
    function onKey(event) {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose();
      } else if (event.key === "ArrowRight") {
        event.preventDefault();
        onStep(1);
      } else if (event.key === "ArrowLeft") {
        event.preventDefault();
        onStep(-1);
      } else if (event.key === "Tab") {
        const nodes = [...(dialogRef.current?.querySelectorAll(FOCUSABLE) ?? [])];
        if (!nodes.length) return;
        const first = nodes[0];
        const last = nodes[nodes.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        } else if (!dialogRef.current?.contains(document.activeElement)) {
          event.preventDefault();
          first.focus();
        }
      }
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose, onStep]);

  const noun = item.kind === "video" ? "Clip" : "Photograph";

  return createPortal(
    <motion.div
      className="st-lightbox"
      role="dialog"
      aria-modal="true"
      aria-label={`${noun} ${index + 1} of ${items.length}: ${item.caption}`}
      ref={dialogRef}
      initial={reduce ? false : { backgroundColor: "rgba(0, 0, 0, 0)" }}
      animate={{ backgroundColor: "rgba(0, 0, 0, 1)" }}
      exit={reduce ? undefined : { backgroundColor: "rgba(0, 0, 0, 0)", transition: { duration: 0.35, ease: EASE_OUT } }}
      transition={{ duration: 0.4, ease: EASE_OUT }}
    >
      <div className="st-lightbox__bar">
        <span className="st-lightbox__count" aria-live="polite">{index + 1} / {items.length}</span>
        <button type="button" className="st-lightbox__btn" ref={closeRef} onClick={onClose} aria-label="Close (Esc)">
          <X weight="bold" aria-hidden="true" />
        </button>
      </div>
      <div className="st-lightbox__stage" onClick={(event) => event.target === event.currentTarget && onClose()}>
        <AnimatePresence mode="popLayout" initial={false}>
          <motion.div
            key={item.id}
            className="st-lightbox__img"
            layoutId={shared ? `st-photo-${item.id}` : undefined}
            style={{ aspectRatio: `${item.width} / ${item.height}`, borderRadius: 20, "--st-ratio": item.width / item.height }}
            initial={shared || reduce ? false : { opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={reduce ? undefined : { opacity: 0, transition: { duration: 0.2 } }}
            transition={{ duration: 0.55, ease: EASE_OUT }}
          >
            {item.kind === "video" ? (
              <video
                src={item.video}
                poster={item.poster}
                width={item.width}
                height={item.height}
                controls
                muted
                loop
                playsInline
                autoPlay={!reduce}
                preload="metadata"
                aria-label={item.alt}
              />
            ) : (
              <img src={item.full} alt={item.alt} width={item.width} height={item.height} decoding="async" />
            )}
          </motion.div>
        </AnimatePresence>
      </div>
      <motion.div
        className="st-lightbox__foot"
        initial={reduce ? false : { opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        exit={reduce ? undefined : { opacity: 0 }}
        transition={{ duration: 0.5, delay: 0.15, ease: EASE_OUT }}
      >
        <button type="button" className="st-lightbox__btn" onClick={() => onStep(-1)} aria-label="Previous">
          <ArrowLeft weight="bold" aria-hidden="true" />
        </button>
        <div className="st-lightbox__caption">
          <p className="st-mono">{itemLabel(item)}{item.kind === "video" ? ` · ${formatDuration(item.duration)} · Silent` : ""}</p>
          <p>{item.caption}</p>
          <p className="st-lightbox__source">
            <Link to={item.href} onClick={onClose}>{item.linkLabel}</Link>
            {item.programme === "AAROH" && <> · <a href={AAROH_ALBUM_URL} target="_blank" rel="noreferrer">Full AAROH album</a></>}
            {" · "}<a href={item.full} target="_blank" rel="noreferrer">{item.kind === "video" ? "Video file" : "Full size"}</a>
          </p>
        </div>
        <button type="button" className="st-lightbox__btn" onClick={() => onStep(1)} aria-label="Next">
          <ArrowRight weight="bold" aria-hidden="true" />
        </button>
      </motion.div>
    </motion.div>,
    document.body,
  );
}

function TileMedia({ item, live }) {
  const videoRef = useRef(null);
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return undefined;
    playInline(video);
    return () => pauseInline(video);
  }, [live]);

  if (item.kind === "video") {
    return (
      <>
        <img src={item.poster} alt="" width={item.width} height={item.height} loading="lazy" decoding="async" />
        {live && <video ref={videoRef} className="st-tile__video" src={item.video} muted loop playsInline preload="auto" aria-hidden="true" />}
        <span className="st-tile__badge" aria-hidden="true">
          <Play weight="fill" />
          {formatDuration(item.duration)}
        </span>
      </>
    );
  }
  if (item.album) {
    return <img src={item.grid} alt="" width={item.width} height={item.height} loading="lazy" decoding="async" />;
  }
  return <Photo src={item.grid} alt="" width={item.width} height={item.height} sizes="(max-width: 700px) 50vw, 30vw" style={item.tileHeight ? { objectPosition: "50% 0%" } : undefined} />;
}

function Tile({ item, index, onOpen, hidden }) {
  const reduce = useReducedMotion();
  const [live, setLive] = useState(false);
  const ratio = item.width / (item.tileHeight ?? item.height);
  const canPreview = item.kind === "video" && !reduce;
  const preview = canPreview ? { onPointerEnter: () => setLive(true), onPointerLeave: () => setLive(false), onFocus: () => setLive(true), onBlur: () => setLive(false) } : {};
  return (
    <motion.li
      layout={reduce ? false : "position"}
      className={`st-tile ${item.kind === "video" ? "st-tile--video" : ""}`}
      style={{ flexGrow: ratio, flexBasis: `calc(var(--st-row) * ${ratio})` }}
      initial={reduce ? false : { opacity: 0, clipPath: "inset(100% 0% 0% 0% round 20px)" }}
      animate={{ opacity: 1, clipPath: "inset(0% 0% 0% 0% round 20px)", transitionEnd: { clipPath: "none" } }}
      exit={reduce ? undefined : { opacity: 0, transition: { duration: 0.18 } }}
      transition={{ duration: 0.8, delay: Math.min(index, 8) * 0.04, ease: EASE_OUT }}
    >
      <button
        type="button"
        data-photo-id={item.id}
        onClick={() => {
          setLive(false);
          onOpen(item.id);
        }}
        aria-label={`${item.kind === "video" ? "Play clip" : "Open photograph"}: ${item.caption}`}
        {...preview}
      >
        <motion.span
          className="st-tile__img"
          layoutId={reduce ? undefined : `st-photo-${item.id}`}
          style={{ aspectRatio: ratio, borderRadius: 20, visibility: hidden ? "hidden" : "visible" }}
          transition={{ duration: 0.55, ease: EASE_OUT }}
        >
          <TileMedia item={item} live={live && !hidden} />
        </motion.span>
        <span className="st-tile__cap">
          <strong>{itemLabel(item)}</strong>
          <span>{item.caption}</span>
        </span>
      </button>
    </motion.li>
  );
}

function countFor(filter, sub) {
  return MEDIA.filter((item) => {
    if (filter !== "All" && item.programme !== filter) return false;
    if (!sub || sub === "all") return true;
    if (sub === "video") return item.kind === "video";
    return item.category === sub;
  }).length;
}

export default function PhotosPage() {
  const reduce = useReducedMotion();
  const [params] = useSearchParams();
  const [filter, setFilter] = useState(() => (PHOTO_FILTERS.includes(params.get("filter")) ? params.get("filter") : "All"));
  const [sub, setSub] = useState(() => (AAROH_SUBFILTERS.some(([key]) => key === params.get("sub")) ? params.get("sub") : "all"));
  const [openId, setOpenId] = useState(null);
  const [currentId, setCurrentId] = useState(null);
  const currentRef = useRef(null);
  currentRef.current = currentId;

  const activeSub = filter === "AAROH" ? sub : "all";
  const visible = useMemo(
    () =>
      MEDIA.filter((item) => {
        if (filter !== "All" && item.programme !== filter) return false;
        if (activeSub === "all") return true;
        if (activeSub === "video") return item.kind === "video";
        return item.category === activeSub;
      }),
    [filter, activeSub],
  );
  const counts = useMemo(() => Object.fromEntries(PHOTO_FILTERS.map((name) => [name, countFor(name)])), []);
  const subCounts = useMemo(() => Object.fromEntries(AAROH_SUBFILTERS.map(([key]) => [key, countFor("AAROH", key)])), []);
  const clipCount = useMemo(() => MEDIA.filter((item) => item.kind === "video").length, []);
  const currentIndex = currentId ? visible.findIndex((item) => item.id === currentId) : -1;

  const open = useCallback((id) => {
    setOpenId(id);
    setCurrentId(id);
  }, []);
  const close = useCallback(() => {
    // Focus returns to the tile of the item that was on screen, not the one first opened.
    const lastId = currentRef.current;
    setCurrentId(null);
    window.setTimeout(() => {
      setOpenId(null);
      document.querySelector(`[data-photo-id="${lastId}"]`)?.focus({ preventScroll: true });
    }, reduce ? 0 : 620);
  }, [reduce]);
  const step = useCallback(
    (direction) => {
      setCurrentId((id) => {
        const index = visible.findIndex((item) => item.id === id);
        if (index < 0) return id;
        return visible[(index + direction + visible.length) % visible.length].id;
      });
    },
    [visible],
  );

  function pickFilter(name) {
    setFilter(name);
    setSub("all");
  }

  const gallerySchema = useMemo(
    () => ({
      "@context": "https://schema.org",
      "@type": "ImageGallery",
      "@id": `${SITE_URL}/media#gallery`,
      url: `${SITE_URL}/media`,
      name: "Project GRID Media Library",
      description:
        "Official Project GRID logos and documented images from student programmes, founders, builders and community events.",
      publisher: { "@id": `${SITE_URL}/#organization` },
      mainEntity: {
        "@type": "ItemList",
        itemListElement: MEDIA_LIBRARY.map((item, index) => ({
          "@type": "ListItem",
          position: index + 1,
          item: {
            "@type": "ImageObject",
            "@id": `${SITE_URL}/media#${item.id}`,
            name: item.caption,
            caption: item.caption,
            contentUrl: `${SITE_URL}${item.fallback}`,
            thumbnailUrl: `${SITE_URL}${item.src.replace(/\.avif$/, "-640.avif")}`,
            width: item.width,
            height: item.height,
            representativeOfPage: index === 0,
          },
        })),
      },
    }),
    [],
  );

  return (
    <PageFrame className="rd-page st-page">
      <Seo
        title="Project GRID Media Library | Official Logos and Photos"
        path="/media"
        description="Download official Project GRID logos and explore captioned images from student programmes, founders, builders and community events."
        schema={gallerySchema}
      />
      <main className="st-photos">
        <header className="st-photos__head">
          <p className="st-mono">Photos</p>
          <MaskWords as="h1" text="Photographs from GRID programmes." onMount delay={0.05} />
          <div className="st-photos__intro">
            <p>
              {MEDIA.length - clipCount} photographs and {clipCount} short silent clips, each with its programme context. Open any of them to see the caption and the page that explains it.
            </p>
            <a className="gh-link" href="#st-logos">Looking for the GRID logo? <ArrowDown weight="bold" aria-hidden="true" /></a>
          </div>
        </header>

        <div className="st-filterbar">
          <div className="st-filter" role="group" aria-label="Filter by programme">
            {PHOTO_FILTERS.map((name) => (
              <button
                type="button"
                key={name}
                aria-pressed={filter === name}
                className={filter === name ? "is-on" : undefined}
                onClick={() => pickFilter(name)}
              >
                {name}<span>{counts[name]}</span>
              </button>
            ))}
          </div>
          <AnimatePresence initial={false}>
            {filter === "AAROH" && (
              <motion.div
                key="sub"
                className="st-subfilter"
                role="group"
                aria-label="Filter AAROH by moment"
                initial={reduce ? false : { opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={reduce ? undefined : { opacity: 0, height: 0 }}
                transition={{ duration: 0.4, ease: EASE_OUT }}
              >
                {AAROH_SUBFILTERS.map(([key, label]) => (
                  <button
                    type="button"
                    key={key}
                    aria-pressed={sub === key}
                    className={sub === key ? "is-on" : undefined}
                    onClick={() => setSub(key)}
                  >
                    {key === "video" && <Play weight="fill" aria-hidden="true" />}
                    {label}<span>{subCounts[key]}</span>
                  </button>
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
        <p className="st-filter__note" aria-live="polite">
          {FILTER_NOTES[filter]}
          {filter === "AAROH" && <> <a href={AAROH_ALBUM_URL} target="_blank" rel="noreferrer">Open the full album <ArrowUpRight weight="bold" aria-hidden="true" /></a></>}
        </p>

        <LayoutGroup id="st-photos">
          <ul className="st-grid" aria-label={`${visible.length} items`}>
            <AnimatePresence mode="popLayout" initial={false}>
              {visible.map((item, index) => (
                <Tile key={item.id} item={item} index={index} onOpen={open} hidden={currentId === item.id && !reduce} />
              ))}
            </AnimatePresence>
          </ul>
          <AnimatePresence>
            {currentId && currentIndex >= 0 && (
              <Lightbox key="lightbox" items={visible} index={currentIndex} openedId={openId} onClose={close} onStep={step} />
            )}
          </AnimatePresence>
        </LayoutGroup>

        <section className="st-logos" id="st-logos" aria-labelledby="st-logos-title">
          <div className="st-logos__copy">
            <MaskWords id="st-logos-title" text="Use the official GRID mark." />
            <p>The square version is prepared for organisation profiles and search results. The original wordmark stays available for wide layouts.</p>
            <div className="gh-actions">
              <a className="gh-btn gh-btn--fill" href={ORGANIZATION_LOGO.src} download="project-grid-organization-logo-1024.png">
                <RollLabel>Download square logo</RollLabel>
                <ArrowDown weight="bold" aria-hidden="true" />
              </a>
              <a className="gh-btn gh-btn--ghost" href="/images/grid-logo-poster-crop.png" download="project-grid-wordmark.png">
                <RollLabel>Download wordmark</RollLabel>
                <ArrowDown weight="bold" aria-hidden="true" />
              </a>
            </div>
          </div>
          <figure className="st-logos__proof">
            <Photo src={ORGANIZATION_LOGO.src} alt={ORGANIZATION_LOGO.alt} width={ORGANIZATION_LOGO.width} height={ORGANIZATION_LOGO.height} sizes="(max-width: 767px) 60vw, 24vw" />
            <figcaption>Project GRID organisation logo on a white background.</figcaption>
          </figure>
        </section>

        <section className="st-usage" aria-labelledby="st-usage-title">
          <h2 id="st-usage-title">Keep the context attached.</h2>
          <div>
            <p>Use the supplied caption, link back to its Project GRID source page and do not imply an endorsement or partnership that the source page does not confirm.</p>
            <p>There is no blanket reuse licence. For a correction, a removal request or permission to reuse a photograph or clip, write to us and name the image.</p>
            <a className="gh-link" href="mailto:founder@projectgrid.org?subject=Project%20GRID%20media%20request">founder@projectgrid.org <ArrowRight weight="bold" aria-hidden="true" /></a>
          </div>
        </section>
      </main>
    </PageFrame>
  );
}
